import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { X, Loader2, FileSpreadsheet, FileText, TrendingUp, AlertTriangle, Search } from "lucide-react";

const fmtEur = (n) =>
  `${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

/**
 * Modal "Opción más rentable" (iter-71).
 * Renderiza los mismos datos que el endpoint `best-supplier-simulation`
 * en una pantalla revisable + descarga (Excel/PDF). Ayuda a la farmacia
 * a estudiar el diferencial sin cambiar de proveedor.
 */
export default function BestSupplierSimulationModal({ dispId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [onlyOpps, setOnlyOpps] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/dispensations/${dispId}/best-supplier-simulation`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Error cargando la simulación"))
      .finally(() => setLoading(false));
  }, [dispId]);

  const months = data?.months || 1;

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.molecules || [];
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.gh.toLowerCase().includes(q) || (r.best_lab || "").toLowerCase().includes(q) || (r.current_lab || "").toLowerCase().includes(q));
    if (onlyOpps) rows = rows.filter((r) => (r.saving_eur || 0) > 0);
    return rows;
  }, [data, query, onlyOpps]);

  const totals = data?.totals || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-stretch" data-testid="best-supplier-modal">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-violet-700 font-bold mb-0.5">Simulación · Rentabilidad</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">
              Opción más rentable por molécula
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              Comparativa lab actual vs. mejor lab disponible según tus convenios cargados. Sin cambiar nada.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                downloadXlsx(
                  `/dispensations/${dispId}/best-supplier-simulation?export=xlsx`,
                  "simulacion_rentable.xlsx",
                )
              }
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
              data-testid="best-supplier-download-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4" /> Descargar Excel
            </button>
            <button
              onClick={() =>
                downloadXlsx(
                  `/dispensations/${dispId}/best-supplier-simulation?export=pdf`,
                  "simulacion_rentable.pdf",
                )
              }
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
              data-testid="best-supplier-download-pdf"
            >
              <FileText className="h-4 w-4" /> Descargar PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
              data-testid="best-supplier-close"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="border-b border-slate-200 px-6 py-4 flex-shrink-0 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Ahorro €/mes" value={fmtEur(totals.saving_per_month_eur)} accent="emerald" data-testid="kpi-saving-month" />
          <KpiCard label="Ahorro €/año" value={fmtEur(totals.saving_per_year_eur)} accent="emerald" />
          <KpiCard label="€ neto actual/mes" value={fmtEur(totals.current_per_month_eur)} accent="slate" />
          <KpiCard label="€ neto óptimo/mes" value={fmtEur(totals.best_per_month_eur)} accent="sky" />
          <KpiCard label="Con ahorro" value={`${totals.n_with_saving || 0} / ${totals.n_molecules || 0}`} accent="slate" />
          <KpiCard label="Requieren alta prov." value={`${totals.n_requires_new_supplier || 0}`} accent="rose" />
        </div>

        {/* Toolbar */}
        <div className="border-b border-slate-200 px-6 py-3 flex flex-wrap gap-3 items-center flex-shrink-0">
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-md border border-slate-200 min-w-[240px]">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar molécula o lab…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="best-supplier-search"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyOpps}
              onChange={(e) => setOnlyOpps(e.target.checked)}
              className="rounded"
              data-testid="best-supplier-only-opps"
            />
            Sólo con ahorro € potencial
          </label>
          <div className="ml-auto text-xs text-slate-500">
            {data ? `${filtered.length} filas · ${months} mes(es) de estudio` : ""}
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-12 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando simulación…
            </div>
          )}
          {err && <div className="p-12 text-center text-rose-600" data-testid="best-supplier-error">{err}</div>}
          {data && !loading && (
            <table className="w-full text-xs border-collapse" data-testid="best-supplier-table">
              <thead className="sticky top-0 bg-slate-900 text-white">
                <tr>
                  <th className="text-left px-3 py-2">Molécula (G.H.)</th>
                  <th className="text-left px-3 py-2">Lab actual</th>
                  <th className="text-right px-3 py-2">% actual</th>
                  <th className="text-right px-3 py-2">€/mes actual</th>
                  <th className="text-left px-3 py-2">Mejor lab</th>
                  <th className="text-right px-3 py-2">% mejor</th>
                  <th className="text-right px-3 py-2 text-emerald-300">Ahorro €/mes</th>
                  <th className="text-left px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-400">
                      No hay moléculas con esos filtros.
                    </td>
                  </tr>
                )}
                {filtered.map((r, idx) => {
                  const monthCurrent = (r.current_net_eur || 0) / months;
                  const monthSaving = (r.saving_eur || 0) / months;
                  const requiresNewSupplier = r.saving_eur > 0 && !r.active_in_vademecum;
                  return (
                    <tr
                      key={r.gh}
                      className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"} ${requiresNewSupplier ? "bg-rose-50/60" : ""}`}
                      data-testid={`best-supplier-row-${idx}`}
                    >
                      <td className="px-3 py-2 max-w-[380px] truncate font-medium" title={r.gh}>
                        {r.gh}
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-[180px] truncate" title={r.current_lab}>
                        {r.current_lab}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtPct(r.current_pct)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtEur(monthCurrent)}</td>
                      <td className="px-3 py-2 max-w-[180px] truncate" title={r.best_lab}>
                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                          {r.best_lab}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-semibold">
                        {fmtPct(r.best_pct)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">
                        {monthSaving > 0 ? (
                          <span className="text-emerald-700 font-bold">+{fmtEur(monthSaving)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {requiresNewSupplier ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 border border-rose-200 px-2 py-0.5 rounded-full">
                            <AlertTriangle className="h-3 w-3" /> Requiere alta
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">
                            OK
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Legend */}
        <div className="border-t border-slate-200 px-6 py-2 text-[11px] text-slate-500 flex flex-wrap gap-4 items-center flex-shrink-0">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-rose-50 border border-rose-300 inline-block" />
            El mejor lab requiere dar de alta un proveedor nuevo
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-emerald-700">
            <TrendingUp className="h-3 w-3" />
            Sólo compara moléculas genéricas con % configurado en tus convenios
          </span>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent = "slate" }) {
  const clsMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    slate: "bg-white border-slate-200 text-slate-800",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${clsMap[accent] || clsMap.slate}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</div>
      <div className="text-lg font-mono-num font-bold mt-1">{value}</div>
    </div>
  );
}
